import { recordingStartedSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';
import { Channel, invoke } from '@tauri-apps/api/core';
import { v4 as uuidv4 } from 'uuid';

import { commands as dbCommands } from '../tauri/plugins/db';
import {
  commands as listenerCommands,
  events as listenerEvents,
  type SessionEvent as ListenerSessionEvent,
} from '../tauri/plugins/listener';
import type { IAudioHandler } from './audio-handler-interface';
import type { TranscriptionService } from './transcription-service';

export type AudioSource = 'Unknown' | 'Mic' | 'Speaker';

export type TimelineViewItem = {
  start: number;
  end: number;
  speaker: number;
  text: string;
  confidence: number;
  vendor: 'WebSocket' | 'Deepgram' | 'AwsTranscribe';
  audio_source: AudioSource;
};

export type TimelineView = { items: TimelineViewItem[] };

type SafeTimelineViewItem = {
  start: number;
  end: number;
  speaker: number;
  text: string;
  confidence: number;
  is_final?: boolean;
  vendor: 'WebSocket' | 'Deepgram' | 'AwsTranscribe';
  audio_source: AudioSource;
};

type DbSession = {
  id: string;
  created_at: string;
  visited_at: string;
  user_id: string;
  title: string;
  raw_memo_html: string;
  enhanced_memo_html: string | null;
  conversations: TimelineViewItem[];
  calendar_event_id: string | null;
};

export interface PublishTauriTranscriptDeps {
  transcriptionService: TranscriptionService;
}

type ListenerStartFailure = {
  code?: string;
  message?: string;
  stage?: string;
  source?: string;
  recoverable?: boolean;
  is_credential_issue?: boolean;
  raw_error?: string;
};

export class TauriAudioHandler implements IAudioHandler {
  private sessionId: string | null = null;
  private chanRef: Channel<ListenerSessionEvent> | null = null;
  private tauriTranscriptionListener:
    | ((event: ListenerSessionEvent) => void)
    | null = null;
  private startAttemptId = 0;
  private activeStartAttemptId: number | null = null;
  private suppressInactiveUntilMs = 0;
  private recentFailureFingerprint: string | null = null;
  private recentFailureTimestampMs: number | null = null;
  private permissionPollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private silentRetryCount = 0;
  private silentRetryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static readonly MAX_SILENT_RETRIES = 8;
  private static readonly RETRY_BACKOFF_BASE_MS = 2000;
  private static readonly RETRY_BACKOFF_MAX_MS = 30000;
  private static readonly FAILURE_DEDUPE_WINDOW_MS = 1500;
  private static readonly INACTIVE_SUPPRESSION_WINDOW_MS = 1500;
  private static readonly START_FAILURE_PREFIX = 'LISTENER_START_FAILURE:';

  private statusEventUnlisten: (() => void) | null = null;

  constructor(private readonly transcriptionService: TranscriptionService) {
    // Listen to the Rust FSM StatusEvent as a safety net.
    // The Channel-based Stopped event can be lost if teardown clears channels
    // before broadcast (now fixed in Rust). This listener ensures we always
    // detect backend-initiated session death even if the Channel path fails.
    listenerEvents.statusEvent
      .listen(event => {
        if (
          event.payload === 'inactive' &&
          this.isRecordingRequested() &&
          !this.shouldSuppressInactiveTransition() &&
          this.activeStartAttemptId === null
        ) {
          this.handleRecoverableFailure('backend_inactive', {
            detected_via: 'status_event_listener',
            failure_stage: 'status_event',
            backend_state: event.payload,
          });
        }
      })
      .then(unlisten => {
        this.statusEventUnlisten = unlisten;
      })
      .catch(error => {
        console.error(
          '[TauriAudioHandler] Failed to listen to StatusEvent:',
          error
        );
      });
  }

  private isStartAttemptActive(attemptId: number): boolean {
    return attemptId === this.startAttemptId;
  }

  private isRecordingRequested(): boolean {
    return this.transcriptionService.transcriptionStatus$.value === 'recording';
  }

  private clearRestartTimers(): void {
    this.clearSilentRetryTimeout();
  }

  private clearPermissionPollTimeout(): void {
    if (this.permissionPollTimeoutId) {
      clearTimeout(this.permissionPollTimeoutId);
      this.permissionPollTimeoutId = null;
    }
  }

  private cancelPendingStart(): void {
    this.startAttemptId += 1;
    this.activeStartAttemptId = null;
    this.clearPermissionPollTimeout();
  }

  private clearSilentRetryTimeout(): void {
    if (this.silentRetryTimeoutId) {
      clearTimeout(this.silentRetryTimeoutId);
      this.silentRetryTimeoutId = null;
    }
  }

  private resetRetryState(): void {
    if (this.silentRetryCount > 0) {
      this.silentRetryCount = 0;
    }
    this.clearSilentRetryTimeout();
    this.recentFailureFingerprint = null;
    this.recentFailureTimestampMs = null;
  }

    private captureTranscriptionFailure(
    _reason: string,
    _details?: Record<string, unknown>
  ): void {
    //
  }

  private captureTranscriptionRecovered(
    _reason: string,
    _details?: Record<string, unknown>
  ): void {
    //
  }

  private setActiveStartAttempt(attemptId: number): void {
    this.activeStartAttemptId = attemptId;
  }

  private clearActiveStartAttempt(attemptId?: number): void {
    if (attemptId === undefined || this.activeStartAttemptId === attemptId) {
      this.activeStartAttemptId = null;
    }
  }

  private suppressInactiveTransition(
    windowMs = TauriAudioHandler.INACTIVE_SUPPRESSION_WINDOW_MS
  ): void {
    this.suppressInactiveUntilMs = Math.max(
      this.suppressInactiveUntilMs,
      Date.now() + windowMs
    );
  }

  private shouldSuppressInactiveTransition(): boolean {
    return Date.now() < this.suppressInactiveUntilMs;
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }

  private parseListenerStartFailure(error: unknown): ListenerStartFailure | null {
    const raw = this.getErrorMessage(error);
    const prefixIndex = raw.indexOf(TauriAudioHandler.START_FAILURE_PREFIX);
    const candidate =
      prefixIndex >= 0
        ? raw.slice(
            prefixIndex + TauriAudioHandler.START_FAILURE_PREFIX.length
          )
        : raw.trimStart().startsWith('{')
          ? raw.trim()
          : null;

    if (!candidate) {
      return null;
    }

    try {
      const parsed = JSON.parse(candidate) as ListenerStartFailure;
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    } catch {
      return null;
    }

    return null;
  }

  private buildFailureFingerprint(
    reason: string,
    details?: Record<string, unknown>
  ): string {
    return JSON.stringify({
      reason,
      session_id: this.sessionId,
      attempt_id: this.activeStartAttemptId ?? this.startAttemptId,
      backend_error_code: details?.['backend_error_code'],
      backend_error_message: details?.['backend_error_message'],
      rust_stop_reason: details?.['rust_stop_reason'],
      error: details?.['error'],
      failure_stage: details?.['failure_stage'],
    });
  }

  private shouldSuppressFailure(
    reason: string,
    details?: Record<string, unknown>
  ): boolean {
    const fingerprint = this.buildFailureFingerprint(reason, details);
    const now = Date.now();

    if (
      this.recentFailureFingerprint === fingerprint &&
      this.recentFailureTimestampMs &&
      now - this.recentFailureTimestampMs <
        TauriAudioHandler.FAILURE_DEDUPE_WINDOW_MS
    ) {
      return true;
    }

    this.recentFailureFingerprint = fingerprint;
    this.recentFailureTimestampMs = now;
    return false;
  }

  private handleRecoverableFailure(
    reason: string,
    details?: Record<string, unknown>
  ): void {
    if (!this.isRecordingRequested()) {
      return;
    }

    if (this.shouldSuppressFailure(reason, details)) {
      return;
    }

    const retryCountBefore = this.silentRetryCount;
    if (retryCountBefore >= TauriAudioHandler.MAX_SILENT_RETRIES) {
      this.captureTranscriptionFailure(reason, {
        retry_count: retryCountBefore,
        recovery_action: 'pause_transcription',
        ...details,
      });
      this.resetRetryState();
      this.transcriptionService.pauseTranscription();
      return;
    }

    const nextRetryNumber = retryCountBefore + 1;
    const rawBackoffMs =
      TauriAudioHandler.RETRY_BACKOFF_BASE_MS *
      Math.pow(2, nextRetryNumber - 1);
    const backoffMs = Math.min(
      rawBackoffMs,
      TauriAudioHandler.RETRY_BACKOFF_MAX_MS
    );

    this.silentRetryCount = nextRetryNumber;
    this.captureTranscriptionFailure(reason, {
      retry_count: retryCountBefore,
      recovery_action: 'retry_scheduled',
      retry_number: nextRetryNumber,
      backoff_ms: backoffMs,
      ...details,
    });

    this.clearSilentRetryTimeout();
    this.silentRetryTimeoutId = setTimeout(() => {
      this.silentRetryTimeoutId = null;
      if (!this.isRecordingRequested()) return;
      this.attemptSilentRestart(reason, details).catch(() => {});
    }, backoffMs);
  }

  private async attemptSilentRestart(
    reason: string,
    details?: Record<string, unknown>
  ): Promise<void> {
    try {
      if (this.chanRef) {
        await this.resetTauriState(this.chanRef);
        this.chanRef = null;
        this.tauriTranscriptionListener = null;
      }

      // Always force a fresh fetch — the vault creds are likely stale/expired
      await this.transcriptionService
        .fetchAndScheduleAwsCredentials()
        .catch(() => {
          // Non-fatal: executeStart will try again and Rust keeps retrying AWS.
        });

      if (!this.isRecordingRequested()) return;

      const attemptId = ++this.startAttemptId;
      this.clearRestartTimers();
      this.sessionId = uuidv4();
      const started = await this.executeStart(attemptId);
      if (!started) {
        return;
      }

      this.captureTranscriptionRecovered(reason, {
        retry_number: this.silentRetryCount,
        new_session_id: this.sessionId,
        ...details,
      });
    } catch (error) {
      this.handleRecoverableFailure('retry_failed', {
        original_reason: reason,
        retry_number: this.silentRetryCount,
        error: this.getErrorMessage(error),
        ...details,
      });
    }
  }

  private isValidTimelineItem(item: unknown): item is SafeTimelineViewItem {
    if (!item || typeof item !== 'object') return false;
    const i = item as Record<string, unknown>;
    const textOk =
      typeof i['text'] === 'string' && (i['text'] as string).trim() !== '';
    const startOk = typeof i['start'] === 'number';
    const endOk = typeof i['end'] === 'number';
    const speakerOk = typeof i['speaker'] === 'number';
    const confOk = typeof i['confidence'] === 'number';
    const finalOk =
      i['is_final'] === undefined || typeof i['is_final'] === 'boolean';
    const audioSourceOk =
      typeof i['audio_source'] === 'string' &&
      ['Unknown', 'Mic', 'Speaker'].includes(i['audio_source'] as string);
    return (
      textOk &&
      startOk &&
      endOk &&
      speakerOk &&
      confOk &&
      finalOk &&
      audioSourceOk
    );
  }

  private getSafeTimelineItems(timeline: unknown): SafeTimelineViewItem[] {
    if (
      !timeline ||
      typeof timeline !== 'object' ||
      !Array.isArray((timeline as { items?: unknown }).items)
    ) {
      return [];
    }

    return (timeline as { items: unknown[] }).items.filter(
      this.isValidTimelineItem.bind(this)
    );
  }

  private isTimelineViewEvent(
    event: unknown
  ): event is { type: 'timelineView'; timeline: unknown } {
    if (!event || typeof event !== 'object') return false;
    const e = event as { type?: unknown };
    return e.type === 'timelineView' && 'timeline' in (event as object);
  }

  private async resetTauriState(
    chanRef?:
      | React.RefObject<Channel<ListenerSessionEvent> | null>
      | Channel<ListenerSessionEvent>,
    cleanupOnly = false
  ): Promise<void> {
    // 1. First, stop the session to prevent new events
    if (!cleanupOnly) {
      try {
        // Check current state first
        const currentState = await listenerCommands.getState();
        if (currentState !== 'inactive') {
          this.suppressInactiveTransition();
          await listenerCommands.stopSession();
          // Give a moment for the backend to stop sending events
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (e) {
        console.warn('[Audio] Error stopping session during reset:', e);
      }
    }

    // 2. Then clean up the channel
    if (chanRef) {
      try {
        const channel = 'current' in chanRef ? chanRef.current : chanRef;
        if (channel) {
          await listenerCommands.unsubscribe(channel);
        }
      } catch (e) {
        console.warn('[Audio] Error unsubscribing from channel:', e);
      }

      if ('current' in chanRef) {
        chanRef.current = null;
      }
    }
  }

  private createTauriTranscriptListener(
    deps: PublishTauriTranscriptDeps
  ): (event: ListenerSessionEvent) => void {
    let sessionStartTimeMs: number | null = null;

    return (event: ListenerSessionEvent): void => {
      if (!this.isTimelineViewEvent(event)) {
        return;
      }

      const { transcriptionService } = deps;

      if (!transcriptionService) return;

      const currentTimeMs = Date.now();

      if (sessionStartTimeMs === null) {
        sessionStartTimeMs = currentTimeMs;
      }

      const safeItems = this.getSafeTimelineItems(event.timeline);
      if (safeItems.length === 0) return;

      const latestItem = safeItems[safeItems.length - 1];
      if (!latestItem) return;

      if (sessionStartTimeMs === null) {
        console.error(
          '[TauriTranscriptListener] sessionStartTimeMs is null when processing timeline event.'
        );
        return;
      }
      const timestampSecs = (currentTimeMs - sessionStartTimeMs) / 1000;

      try {
        const isFinal = Boolean(latestItem.is_final);

        const audioSource =
          latestItem.audio_source === 'Mic'
            ? 'mic'
            : latestItem.audio_source === 'Speaker'
              ? 'speaker'
              : undefined;

        transcriptionService.setLatestTranscriptionChunk({
          text: latestItem.text,
          timestamp: timestampSecs,
          isFinal: isFinal,
          vendor: latestItem.vendor,
          audioSource,
        });

        import('@pique/core/blocksuite/presets/ai')
          .then(({ AIProvider }) => {
            AIProvider.slots.toggleChatCards.emit({ visible: true, ok: true });
          })
          .catch(error => {
            console.warn(
              '[TauriAudioHandler] Error importing AIProvider:',
              error
            );
          });
      } catch (error) {
        console.warn(
          '[TauriTranscriptListener] Error processing transcription chunk:',
          error
        );
      }
    };
  }

  private async ensureAudioPermissions(attemptId: number): Promise<boolean> {
    if (!environment.isTauri) return true;
    try {
      if (!this.isStartAttemptActive(attemptId) || !this.isRecordingRequested())
        return false;

      // 1) Microphone access: check → request (OS prompt) → recheck
      let micGranted = false;
      try {
        micGranted = await listenerCommands.checkMicrophoneAccess();
      } catch (e) {
        console.warn('[Audio] checkMicrophoneAccess failed:', e);
      }

      if (!micGranted) {
        try {
          await listenerCommands.requestMicrophoneAccess();
        } catch (e) {
          console.warn('[Audio] requestMicrophoneAccess failed:', e);
        }

        // Poll for user response for up to ~12s
        const start = Date.now();
        while (Date.now() - start < 12000) {
          if (
            !this.isStartAttemptActive(attemptId) ||
            !this.isRecordingRequested()
          ) {
            return false;
          }
          try {
            micGranted = await listenerCommands.checkMicrophoneAccess();
          } catch {
            micGranted = false;
          }
          if (micGranted) break;
          await new Promise(resolve => setTimeout(resolve, 400));
        }

        if (!micGranted) {
          // As a last resort, open system settings so user can enable manually
          try {
            await listenerCommands.openMicrophoneAccessSettings();
          } catch (e) {
            console.warn('[Audio] openMicrophoneAccessSettings failed:', e);
          }
        }
      }

      // 2) System audio (speaker capture) – best-effort request to surface OS prompt if needed
      // Run this in parallel with start session, don't block
      listenerCommands.requestSystemAudioAccess().catch(e => {
        console.warn('[Audio] requestSystemAudioAccess failed:', e);
      });

      return micGranted;
    } catch (e) {
      console.warn('[Audio] ensureAudioPermissions unexpected error:', e);
      return false;
    }
  }

  private async waitForMicPermissionAndStart(attemptId: number): Promise<void> {
    const deadline = Date.now() + 30000;
    while (Date.now() < deadline) {
      if (!this.isStartAttemptActive(attemptId) || !this.isRecordingRequested())
        return;
      try {
        const ok = await listenerCommands.checkMicrophoneAccess();
        if (ok) {
          if (
            !this.isStartAttemptActive(attemptId) ||
            !this.isRecordingRequested()
          ) {
            return;
          }
          await this.executeStart(attemptId);
          return;
        }
      } catch {
        // ignore
      }
      await new Promise(resolve => {
        this.permissionPollTimeoutId = setTimeout(resolve, 800);
      });
      this.permissionPollTimeoutId = null;
    }
    console.warn(
      '[Audio] Microphone permission not granted after waiting; aborting start retry'
    );
    this.handleRecoverableFailure('microphone_permission_timeout', {
      detected_via: 'permission_poll',
      failure_stage: 'permission',
    });
  }

  public async start(): Promise<void> {
    try {
      const attemptId = ++this.startAttemptId;
      this.clearRestartTimers();
      this.clearPermissionPollTimeout();

      const micOk = await this.ensureAudioPermissions(attemptId);
      if (!this.isStartAttemptActive(attemptId) || !this.isRecordingRequested())
        return;

      if (this.chanRef) {
        await this.resetTauriState(this.chanRef);
        this.chanRef = null;
        this.tauriTranscriptionListener = null;
      }

      try {
        const micMuted = await listenerCommands.getMicMuted();
        const spkMuted = await listenerCommands.getSpeakerMuted();

        if (micMuted) await listenerCommands.setMicMuted(false);
        if (spkMuted) await listenerCommands.setSpeakerMuted(false);
      } catch (error) {
        console.warn('[Audio] Error checking device mute states:', error);
      }

      this.sessionId = uuidv4();

      if (micOk) {
        await this.executeStart(attemptId);
        return;
      }

      await this.waitForMicPermissionAndStart(attemptId);
      return;
    } catch (e) {
      this.clearActiveStartAttempt();
      if (this.chanRef) {
        const channel = this.chanRef;
        await this.resetTauriState(channel);
        this.chanRef = null;
        this.tauriTranscriptionListener = null;
        this.sessionId = null;
      }

      this.transcriptionService.setAmplitude({
        mic: 0,
        speaker: 0,
        scaled: 0,
      });
      this.handleRecoverableFailure('start_failed', {
        detected_via: 'start',
        failure_stage: 'start',
        error: this.getErrorMessage(e),
      });
    }
  }

  private async executeStart(attemptId: number): Promise<boolean> {
    try {
      if (!this.isStartAttemptActive(attemptId) || !this.isRecordingRequested())
        return false;
      this.setActiveStartAttempt(attemptId);

      const credsOk = await this.transcriptionService.ensureFreshCredentials();
      if (!credsOk) {
        console.warn(
          '[Audio] AWS credentials fetch failed, proceeding anyway (Rust AWS reconnection loop will retry)'
        );
      }

      let userId: string;
      try {
        const cfg = await dbCommands.getConfig();
        userId = cfg.user_id;
      } catch (error) {
        console.error('[Audio] Failed to read DB config for user_id:', error);
        throw error;
      }

      const now = new Date().toISOString();

      await invoke('plugin:db|upsert_session', {
        session: {
          id: this.sessionId,
          created_at: now,
          visited_at: now,
          user_id: userId,
          title: `Recording ${now}`,
          raw_memo_html: '',
          enhanced_memo_html: null,
          conversations: [],
          calendar_event_id: null,
        } as DbSession,
      });

      const channel = new Channel<ListenerSessionEvent>();
      this.chanRef = channel;

      this.tauriTranscriptionListener = this.createTauriTranscriptListener({
        transcriptionService: this.transcriptionService,
      });

      channel.onmessage = e => {
        if (this.tauriTranscriptionListener) {
          this.tauriTranscriptionListener(e);
        }

        if (e.type === 'audioAmplitude') {
          const scaled = Math.min(1, Math.max(e.mic, e.speaker) / 5);
          const amplitudeData = { mic: e.mic, speaker: e.speaker, scaled };
          this.transcriptionService.setAmplitude(amplitudeData);
        } else if (e.type === 'timelineView') {
          this.resetRetryState();
        } else if (e.type === 'stopped') {
          if (e.reason !== 'user_requested') {
            if (
              this.activeStartAttemptId !== null &&
              e.reason.startsWith('startup_error:')
            ) {
              return;
            }
            this.handleRecoverableFailure('listener_stopped', {
              detected_via: 'session_channel',
              failure_stage: 'session_event',
              rust_stop_reason: e.reason,
            });
          }
        } else if (e.type === 'credentialExpired') {
          if (this.activeStartAttemptId !== null) {
            return;
          }
          this.handleRecoverableFailure('credential_expired', {
            detected_via: 'session_channel',
            failure_stage: 'credential_refresh',
          });
        }
      };

      await listenerCommands.subscribe(channel);
      await listenerCommands.startSession(this.sessionId || '');
      this.clearActiveStartAttempt(attemptId);

      recordingStartedSlot.emit();

      try {
        const stateAfterStart = await listenerCommands.getState();
        if (stateAfterStart === 'inactive') {
          this.handleRecoverableFailure('post_start_inactive', {
            detected_via: 'post_start_state_check',
            failure_stage: 'post_start_state_check',
          });
          return false;
        }
      } catch (err) {
        this.captureTranscriptionFailure('post_start_state_check_failed', {
          failure_stage: 'state_check_error',
          error: this.getErrorMessage(err),
        });
      }
      return true;
    } catch (e) {
      this.clearActiveStartAttempt(attemptId);
      const listenerFailure = this.parseListenerStartFailure(e);
      const backendReason = listenerFailure?.code ?? 'execute_start_failed';
      const failedSessionId = this.sessionId;

      if (this.chanRef) {
        const channel = this.chanRef;
        await this.resetTauriState(channel);
        this.chanRef = null;
        this.tauriTranscriptionListener = null;
        this.sessionId = null;
      }

      this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
      this.handleRecoverableFailure(backendReason, {
        session_id: failedSessionId,
        detected_via: 'start_command',
        failure_stage: listenerFailure?.stage ?? 'execute_start',
        error: listenerFailure?.message ?? this.getErrorMessage(e),
        backend_error_code: listenerFailure?.code,
        backend_error_message: listenerFailure?.message,
        backend_error_source: listenerFailure?.source,
        backend_error_recoverable: listenerFailure?.recoverable,
        backend_is_credential_issue: listenerFailure?.is_credential_issue,
        backend_raw_error: listenerFailure?.raw_error,
      });
      return false;
    }
  }

  public async stop(): Promise<void> {
    try {
      this.cancelPendingStart();
      this.clearRestartTimers();
      this.resetRetryState();

      if (!this.chanRef) {
        return;
      }

      const channel = this.chanRef;
      await this.resetTauriState(channel);
      this.chanRef = null;
      this.tauriTranscriptionListener = null;
      this.sessionId = null;

      // Reset amplitude only - don't touch transcription state
      this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
    } catch (error) {
      console.error('[TauriAudioHandler] Error stopping recording:', error);
      // Ensure amplitude is reset even on error - don't touch transcription state
      this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
    }
  }

  public getMediaStream(): MediaStream | null {
    // Tauri doesn't expose MediaStream directly
    return null;
  }

  public dispose(): void {
    this.cancelPendingStart();
    this.clearRestartTimers();
    this.resetRetryState();
    if (this.statusEventUnlisten) {
      this.statusEventUnlisten();
      this.statusEventUnlisten = null;
    }
    if (this.chanRef) {
      const channel = this.chanRef;
      this.resetTauriState(channel).catch(console.error);
    }
    this.sessionId = null;
    this.chanRef = null;
    this.tauriTranscriptionListener = null;
    this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
  }
}
